import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import Spinner from '../components/Spinner';
import PropertyItem from '../components/PropertyItem';
import { useParams } from 'react-router-dom';

export default function Category() {
  const [listings, setListings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetchedListing, setLastFetchListing] = useState(null);
  const [sortBy, setSortBy] = useState('timestamp-desc');
  const params = useParams();

  useEffect(() => {
    async function fetchListings() {
      try {
        const listingRef = collection(db, 'listings');
        const q = query(
          listingRef,
          where('type', '==', params.categoryName),
          orderBy('timestamp', 'desc'),
          limit(8)
        );
        const querySnap = await getDocs(q);
        const lastVisible = querySnap.docs[querySnap.docs.length - 1];
        setLastFetchListing(lastVisible);
        const listings = [];
        querySnap.forEach((doc) => {
          return listings.push({
            id: doc.id,
            data: doc.data(),
          });
        });
        setListings(listings);
        setLoading(false);
      } catch (error) {
        toast.error('Could not fetch listing');
      }
    }
    fetchListings();
  }, [params.categoryName]);

  async function onFetchMoreListings() {
    try {
      const listingRef = collection(db, 'listings');
      const q = query(
        listingRef,
        where('type', '==', params.categoryName),
        orderBy('timestamp', 'desc'),
        startAfter(lastFetchedListing),
        limit(4)
      );
      const querySnap = await getDocs(q);
      const lastVisible = querySnap.docs[querySnap.docs.length - 1];
      setLastFetchListing(lastVisible);
      const listings = [];
      querySnap.forEach((doc) => {
        return listings.push({
          id: doc.id,
          data: doc.data(),
        });
      });
      setListings((prevState) => [...prevState, ...listings]);
      setLoading(false);
    } catch (error) {
      toast.error('Could not fetch listing');
    }
  }

  // Sort function
  function handleSort(value) {
    setSortBy(value);
    
    if (!listings) return;
    
    let sortedListings = [...listings];
    
    switch(value) {
      case 'price-asc':
        sortedListings.sort((a, b) => {
          const priceA = a.data.offer ? a.data.discountedPrice : a.data.regularPrice;
          const priceB = b.data.offer ? b.data.discountedPrice : b.data.regularPrice;
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        sortedListings.sort((a, b) => {
          const priceA = a.data.offer ? a.data.discountedPrice : a.data.regularPrice;
          const priceB = b.data.offer ? b.data.discountedPrice : b.data.regularPrice;
          return priceB - priceA;
        });
        break;
      case 'timestamp-desc':
        sortedListings.sort((a, b) => {
          return b.data.timestamp?.seconds - a.data.timestamp?.seconds;
        });
        break;
      case 'timestamp-asc':
        sortedListings.sort((a, b) => {
          return a.data.timestamp?.seconds - b.data.timestamp?.seconds;
        });
        break;
      default:
        break;
    }
    
    setListings(sortedListings);
  }

  return (
    <div className="max-w-6xl mx-auto px-3">
      <h1 className="text-3xl text-center mt-6 font-bold mb-6">
        {params.categoryName === 'rent' ? 'Places for rent' : 'Places for sale'}
      </h1>

      {/* Sort Dropdown */}
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">
          {listings && listings.length > 0 && `${listings.length} properties found`}
        </p>
        <div className="flex items-center gap-2">
          <label htmlFor="sort" className="font-semibold text-gray-700">
            Sort by:
          </label>
          <select
            id="sort"
            value={sortBy}
            onChange={(e) => handleSort(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="timestamp-desc">Newest First</option>
            <option value="timestamp-asc">Oldest First</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : listings && listings.length > 0 ? (
        <>
          <main>
            <ul className="sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {listings.map((listing) => (
                <PropertyItem
                  key={listing.id}
                  id={listing.id}
                  listing={listing.data}
                />
              ))}
            </ul>
          </main>
          {lastFetchedListing && (
            <div className="flex justify-center items-center my-8">
              <button
                onClick={onFetchMoreListings}
                className="bg-white px-6 py-2 text-gray-700 border border-gray-300 hover:border-slate-600 rounded transition duration-150 ease-in-out"
              >
                Load more
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-center text-gray-600 py-20">
          There are no current{' '}
          {params.categoryName === 'rent' ? 'places for rent' : 'places for sale'}
        </p>
      )}
    </div>
  );
}
